import { and, eq, sql } from "drizzle-orm";

import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { tag, workspaceMembershipTag } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery";

export async function bulkUpsertWorkspaceMembershipTag(
  records: { workspaceMembershipId: string; tagId: string; tagValue: string | null; enabled: boolean }[],
) {
  const withValue = records.filter(
    (r): r is { workspaceMembershipId: string; tagId: string; tagValue: string; enabled: boolean } =>
      r.tagValue != null,
  );
  if (withValue.length === 0) return;
  const deduped = deduplicateByKeyValue(
    withValue,
    (item) => `${item.workspaceMembershipId}:${item.tagId}:${item.tagValue}`,
  );
  await bulkInsert(workspaceMembershipTag, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [
        workspaceMembershipTag.workspaceMembershipId,
        workspaceMembershipTag.tagId,
        workspaceMembershipTag.tagValue,
      ],
      set: {
        enabled: sql`"excluded"."enabled"`,
      },
    },
  });
  emitTableUpdates(["workspace_membership_tag"]);
}

export async function replaceAllWorkspaceMembershipTags(
  records: { workspaceMembershipId: string; tagId: string; tagValue: string | null; enabled: boolean }[],
) {
  // Skip disabled rows — the local table is wiped before insert, so there is nothing to tombstone.
  const withValue = records.filter(
    (r): r is { workspaceMembershipId: string; tagId: string; tagValue: string; enabled: boolean } =>
      r.tagValue != null && r.enabled,
  );
  const deduped = deduplicateByKeyValue(
    withValue,
    (item) => `${item.workspaceMembershipId}:${item.tagId}:${item.tagValue}`,
  );

  await db.delete(workspaceMembershipTag);

  if (deduped.length > 0) {
    await bulkInsert(workspaceMembershipTag, deduped, {
      onConflict: { type: "doNothing" },
    });
  }

  emitTableUpdates(["workspace_membership_tag"]);
}

export function allWorkspaceMembershipTagsQuery() {
  const query = db
    .select({
      workspaceMembershipId: workspaceMembershipTag.workspaceMembershipId,
      tagId: workspaceMembershipTag.tagId,
      tagValue: workspaceMembershipTag.tagValue,
      tagLabel: tag.label,
    })
    .from(workspaceMembershipTag)
    .innerJoin(tag, eq(workspaceMembershipTag.tagId, tag.id))
    .where(and(eq(workspaceMembershipTag.enabled, true), eq(tag.enabled, true)));
  logQuery("All Workspace Membership Tags", query);
  return query;
}
